import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import {
  loadStripe,
  PaymentIntentResult,
  PaymentRequest,
  Stripe,
  StripeElementLocale,
  StripeElements,
} from '@stripe/stripe-js';
import {
  BehaviorSubject,
  catchError,
  from,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { ApiService, CurrencyCode } from './api.service';

export interface PaymentIntentHandlerResult {
  success: boolean;
  error?: any;
  paymentIntent?: any;
}

@Injectable({
  providedIn: 'root',
})
export class StripeService {
  // One Stripe instance per publishable key: different flows may target different Stripe
  // accounts (e.g. the credits catalog resolves an existing customer's account and returns
  // its key, while funnel checkouts resolve the account via the bootstrap endpoint).
  private stripeByKey = new Map<string, Observable<Stripe>>();
  private publishableKey$ = new BehaviorSubject<string | null>(null);
  private bootstrapRequest$: Observable<string> | null = null;

  constructor(
    private api: ApiService,
    @Inject(LOCALE_ID) private localeId: string
  ) {}

  /**
   * Kicks off Stripe loading in the background. Without a key the account is resolved via
   * the Stripe bootstrap endpoint; flows that already know their account (e.g. the credits
   * top-up, whose catalog returns publishableKey) pass it explicitly.
   */
  preloadStripe(publishableKey?: string): void {
    this.getStripe(publishableKey)
      .pipe(
        take(1),
        catchError(() => of(null))
      )
      .subscribe();
  }

  /**
   * Creates a payment request object for Apple Pay / Google Pay
   */
  createPaymentRequest(
    amount: number,
    label: string,
    currency: CurrencyCode,
    requestEmail: boolean = false,
    publishableKey?: string
  ): Observable<PaymentRequest> {
    console.log('Creating payment request for:', { amount, label, requestEmail, currency });

    const normalizedCurrency = (currency || 'USD').toLowerCase();

    return this.getStripe(publishableKey).pipe(
      map(stripe =>
        stripe.paymentRequest({
          country: 'US',
          currency: normalizedCurrency,
          total: {
            label: label || 'Total',
            amount: Math.round(amount * 100),
          },
          requestShipping: false,
          requestPayerName: false,
          requestPayerEmail: requestEmail,
        })
      )
    );
  }

  /**
   * Creates payment elements for Stripe Elements
   */
  createPaymentElements(
    amount: number,
    currency: CurrencyCode,
    isDark: boolean,
    mode: 'payment' | 'subscription' = 'subscription',
    publishableKey?: string
  ): Observable<StripeElements> {
    const normalizedCurrency = (currency || 'USD').toLowerCase();

    return this.getStripe(publishableKey).pipe(
      map(stripe =>
        stripe.elements({
          mode,
          // Server-side one-time intents are created with setup_future_usage: 'off_session'
          // (so saved methods can be charged later); deferred Elements must declare the same
          // or stripe.confirmPayment rejects the mismatch ('off_session' vs null). mode:
          // 'subscription' implies off_session by itself, so only payment mode needs it.
          ...(mode === 'payment' ? { setupFutureUsage: 'off_session' as const } : {}),
          currency: normalizedCurrency,
          amount: Math.round(amount * 100),
          locale: this.getStripeLocale(),
          appearance: {
            theme: 'stripe',
            variables: {
              colorBackground: isDark ? '#26272f' : '#fbfbfb',
              colorText: isDark ? '#fbfbfb' : '#26272f',
              fontFamily: `'Poppins', sans-serif`,
              colorPrimary: '#31bc6d',
              colorTextPlaceholder: isDark ? '#FBFBFB8C' : '#26272F8C',
            },
          },
          loader: 'always',
        })
      )
    );
  }

  /**
   * Handles payment with Apple Pay / Google Pay
   */
  confirmExpressPayment(
    clientSecret: string,
    paymentMethod: any,
    publishableKey?: string
  ): Observable<PaymentIntentHandlerResult> {
    return this.getStripe(publishableKey).pipe(
      switchMap(stripe =>
        from(
          stripe.confirmCardPayment(
            clientSecret,
            { payment_method: paymentMethod.id },
            { handleActions: false }
          )
        ).pipe(
          map(({ paymentIntent, error }) => {
            if (error) {
              return { success: false, error };
            }

            return { success: true, paymentIntent };
          }),
          catchError(error => {
            return of({ success: false, error });
          })
        )
      )
    );
  }

  /**
   * Handles additional authentication actions if needed
   */
  handleAdditionalPaymentActions(
    clientSecret: string,
    publishableKey?: string
  ): Observable<PaymentIntentHandlerResult> {
    return this.getStripe(publishableKey).pipe(
      switchMap(stripe =>
        from(stripe.confirmCardPayment(clientSecret)).pipe(
          map(({ paymentIntent, error }) => {
            if (error) {
              return { success: false, error };
            }
            return { success: true, paymentIntent };
          }),
          catchError(error => {
            return of({ success: false, error });
          })
        )
      )
    );
  }

  /**
   * Finishes an intent the server left in `requires_action` — an off-session charge the issuer
   * wants the cardholder to authenticate. The saved card stays valid, so this runs the 3-D
   * Secure challenge on that same intent instead of collecting a new payment method.
   */
  handleNextAction(
    clientSecret: string,
    publishableKey?: string
  ): Observable<PaymentIntentHandlerResult> {
    return this.getStripe(publishableKey).pipe(
      switchMap(stripe =>
        from(stripe.handleNextAction({ clientSecret })).pipe(
          map(({ paymentIntent, error }) => {
            if (error) {
              return { success: false, error };
            }

            // The challenge can end in a refusal: the intent comes back needing a payment
            // method again rather than succeeded, with no error object to go on.
            return { success: paymentIntent?.status === 'succeeded', paymentIntent };
          }),
          catchError(error => of({ success: false, error }))
        )
      )
    );
  }

  /**
   * Handles regular checkout payment confirmation
   */
  confirmPayment(
    elements: StripeElements,
    clientSecret: string,
    returnUrl: string,
    publishableKey?: string
  ): Observable<PaymentIntentResult> {
    return this.getStripe(publishableKey).pipe(
      switchMap(stripe =>
        from(
          stripe.confirmPayment({
            elements,
            clientSecret,
            confirmParams: {
              return_url: returnUrl,
            },
            redirect: 'if_required' as 'if_required',
          })
        )
      )
    );
  }

  private getStripe(publishableKey?: string): Observable<Stripe> {
    if (publishableKey) {
      return this.loadStripeForKey(publishableKey);
    }

    return this.getPublishableKey().pipe(switchMap(key => this.loadStripeForKey(key)));
  }

  private loadStripeForKey(key: string): Observable<Stripe> {
    let stripe$ = this.stripeByKey.get(key);

    if (!stripe$) {
      console.log('Loading Stripe...');

      stripe$ = from(loadStripe(key, { locale: this.getStripeLocale() })).pipe(
        map(stripe => {
          if (!stripe) {
            throw new Error('Stripe failed to initialize.');
          }

          console.log('Stripe loaded successfully');
          return stripe;
        }),
        catchError(error => {
          console.error('Failed to load Stripe', error);
          // Drop the cached failure so a later attempt (e.g. the retry button) starts fresh.
          this.stripeByKey.delete(key);
          return throwError(() => error);
        }),
        shareReplay(1)
      );

      this.stripeByKey.set(key, stripe$);
    }

    return stripe$;
  }

  private getPublishableKey(): Observable<string> {
    if (this.publishableKey$.value) {
      return of(this.publishableKey$.value);
    }

    if (!this.bootstrapRequest$) {
      this.bootstrapRequest$ = this.api.getStripeBootstrap().pipe(
        map(({ publishableKey }) => publishableKey),
        tap(key => {
          this.publishableKey$.next(key);
        }),
        shareReplay(1),
        catchError(error => {
          this.publishableKey$.next(null);
          this.bootstrapRequest$ = null;
          return throwError(() => error);
        })
      );
    }

    return this.bootstrapRequest$;
  }

  /**
   * We currently support EN + ES + DE + FR + PT + IT + PL + TR.
   * Stripe expects a StripeElementLocale; we map Angular LOCALE_ID to:
   * - "es" for any Spanish locale (e.g. "es", "es-ES", "es_MX")
   * - "de" for any German locale (e.g. "de", "de-DE", "de_AT")
   * - "fr" for any French locale (e.g. "fr", "fr-FR", "fr_CA")
   * - "it" for any Italian locale (e.g. "it", "it-IT")
   * - "pl" for any Polish locale (e.g. "pl", "pl-PL")
   * - "tr" for any Turkish locale (e.g. "tr", "tr-TR")
   * - "pt-BR" for Portuguese because our "pt" deployment is Brazilian Portuguese
   * - "en" for everything else (default)
   */
  private getStripeLocale(): StripeElementLocale {
    const normalized = (this.localeId || '').trim().replace('_', '-').toLowerCase();
    if (normalized === 'es' || normalized.startsWith('es-')) return 'es';
    if (normalized === 'de' || normalized.startsWith('de-')) return 'de';
    if (normalized === 'fr' || normalized.startsWith('fr-')) return 'fr';
    if (normalized === 'it' || normalized.startsWith('it-')) return 'it';
    if (normalized === 'pl' || normalized.startsWith('pl-')) return 'pl';
    if (normalized === 'tr' || normalized.startsWith('tr-')) return 'tr';
    if (normalized === 'pt' || normalized.startsWith('pt-')) return 'pt-BR';
    return 'en';
  }
}
